'use client';

import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { domains as domainsFilterObject } from '@components/features/filters/helpers/filterObjects';

interface DetectedFiltersChipsProps {
  decomposedFilters: ApiSchema<'JobSearchRequestFilter'>;
}

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full border border-input bg-surface px-3 py-1 text-sm text-ink">
    {children}
  </span>
);

export const DetectedFiltersChips = ({ decomposedFilters }: DetectedFiltersChipsProps) => {
  const { t } = useTranslation('common');

  const domainLabelById = useMemo(() => {
    const map = new Map<number, string>();
    domainsFilterObject.fields.forEach((field) => {
      const id = Number(field.recordOptions?.defaultValue);
      if (Number.isInteger(id) && field.label) map.set(id, field.label);
    });
    return map;
  }, []);

  const {
    keyword, locations, remote, abroad, domains, minSalary, companyName,
  } = decomposedFilters;

  const chips: React.ReactNode[] = [];

  if (keyword) {
    chips.push(<Chip key="title">{`${t('job.add.label.title')}: ${keyword}`}</Chip>);
  }
  if (locations?.length) {
    chips.push(<Chip key="address">{`${t('job.add.label.job_location')}: ${locations.join(', ')}`}</Chip>);
  }
  if (remote) {
    chips.push(<Chip key="remote">{t('global.label.remote')}</Chip>);
  }
  if (abroad) {
    chips.push(<Chip key="abroad">{t('popular.search.filters.abroad')}</Chip>);
  }
  if (domains?.length) {
    const domainNames = domains
      .map((id) => {
        const key = domainLabelById.get(id);
        return key ? t(key) : null;
      })
      .filter((name): name is string => !!name);
    if (domainNames.length) {
      chips.push(<Chip key="domain">{`${t('global.filter.domain')}: ${domainNames.join(', ')}`}</Chip>);
    }
  }
  if (minSalary) {
    chips.push(<Chip key="salary">{`${t('job.add.label.net_salary')}: ${minSalary} EUR`}</Chip>);
  }
  if (companyName) {
    chips.push(<Chip key="company">{`${t('page.company.billing.label.company.name')}: ${companyName}`}</Chip>);
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {chips}
    </div>
  );
};
